import React, { useEffect, useMemo, useState } from 'react';
import { actionType, initialAddressState } from 'config/constants';
import { Box, Grid, Typography } from '@material-ui/core';
import { BurgerMenu } from 'components/BurgerMenu';
import { HeaderParameters } from '../components';
import { isNil } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { ServiceApi } from 'services';
import { toastMessages } from 'config/constants';
import { useAddresses, useAddressesFunctions } from 'contexts/AddressContext';
import ActionOnAddressModal from 'components/ActionOnAddressModal';
import AddressForm from 'components/AddressForm';
import CustomSelect from 'components/CustomSelect';
import DefaultModal from 'components/DefaultModal';
import place from '_assets/register/Place.svg';
import toast from 'react-hot-toast';
import useShowActionModal from 'hooks/useShowActionModal';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
  },
  dialogContent: {
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    marginTop: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(4),
  },
  modalText: {
    fontWeight: '300',
    fontSize: '24px',
    lineHeight: '28.13px',
    color: theme.palette.tertiary.main,
  },
}));

const DELETE_DIALOG_ENUM = {
  HIDDEN: 'HIDDEN',
  NOT_PRINCIPAL_ADDRESS: 'NOT_PRINCIPAL_ADDRESS',
};

function AddressesParameters() {
  const classes = useStyles();

  const { addresses, currentAddress } = useAddresses();
  const { createAddress, removeAddress, updateAddress } = useAddressesFunctions();
  const firstAddress = useMemo(() => addresses[0] ?? null, [addresses]);

  const [sae, setSae] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [address, setAddress] = useState(currentAddress ?? initialAddressState);
  const [selectedAddress, setSelectedAddress] = useState(address);
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [formDisabled, setFormDisabled] = useState(true);

  const [deleteDialogStatus, setDeleteDialogStatus] = useState(DELETE_DIALOG_ENUM.HIDDEN);

  const {
    previousChoice,
    resetShowAction,
    setActionAddress,
    setActionConflictSelect,
    setActionConflictTransfer,
    setActionSelect,
    shouldShowAction,
    showAction,
  } = useShowActionModal(actionType.DELETE, 'address', deleteAddress, sae);

  useEffect(() => {
    setSae([]);
    if (!isNil(address?.id)) {
      // Check if the selected address has any activities associated
      ServiceApi.getSAEAssociationsByAddress(address?.id).then(({ data }) => {
        setSae(data);
      });
    }
  }, [address?.id]);

  const handleCreateAddress = async (values) => {
    const body = {
      address: values.address_name,
      city: values.city_name,
      title: values.title,
      index: values.street_index,
      number: values.street_number,
      zipCode: values.zip_code,
    };

    const { created, item } = await createAddress(body);
    if (created) {
      setFormDisabled(true);
      setIsNewAddress(false);
      setSelectedAddress(address);
      setAddress(item);
    }

    return { success: created };
  };

  const handleEditAddress = async (values) => {
    let body = {
      address: values.address_name,
      city: values.city_name,
      enabled: values.enabled,
      id: selectedAddress.id,
      title: values.title,
      index: values.street_index,
      number: values.street_number,
      zipCode: values.zip_code,
    };

    const { updated, item } = await updateAddress(body);
    if (updated) {
      setFormDisabled(true);
      setIsNewAddress(false);
      setAddress(item);
    }

    return { success: updated };
  };

  const handleStatusChanged = async ({ action, enabled, force, selectedAddress }) => {
    let body = { enabled, id: address.id };

    if (!enabled) {
      if (['DEACTIVATE', 'DELETE'].includes(action)) body = { ...body, action, force };
      else if (selectedAddress) {
        try {
          const response = await ServiceApi.transferServiceAddress({
            destination: selectedAddress.id,
            source: address.id,
            force,
          });
          if (response.status === 'CONFLICT') return { conflict: true, success: false };
        } catch (_) {
          toast.error(toastMessages.error.TRANSFER_ADDRESS);
          return { success: false };
        }
      }
    }

    const { conflict, updated, item } = await updateAddress(body);
    if (updated) setAddress(item);

    return { conflict, success: updated };
  };

  async function deleteAddress({ action, force, selectedAddress }) {
    let body = {};
    if (['DEACTIVATE', 'DELETE'].includes(action)) body = { action, force };
    else if (selectedAddress) {
      try {
        const response = await ServiceApi.transferServiceAddress({
          destination: selectedAddress.id,
          source: address.id,
          force,
        });
        if (response.status === 'CONFLICT') return { conflict: true, success: false };
      } catch (_) {
        toast.error(toastMessages.error.TRANSFER_ADDRESS);
        return { success: false };
      }
    }

    const { conflict, removed } = await removeAddress(address.id, body);
    if (removed) setAddress(firstAddress ?? initialAddressState);

    return { conflict, success: removed };
  }

  const onAddressClick = (address) => {
    if (isNewAddress || !formDisabled) return;

    setFormDisabled(true);
    setSelectedAddress(address);
    setAddress(address);
  };

  const onAddNewAddressClick = () => {
    if (isNewAddress || !formDisabled) return;

    setAddress({ ...initialAddressState });
    setIsNewAddress(true);
    setFormDisabled(false);
  };

  const onRemoveAddressClick = () => {
    if (currentAddress.id === selectedAddress.id) {
      return setDeleteDialogStatus(DELETE_DIALOG_ENUM.NOT_PRINCIPAL_ADDRESS);
    }

    shouldShowAction();
  };

  const handleDeleteAddress = async (action, force = false) => {
    setActionLoading(true);
    const response = await deleteAddress({ action, force });
    setActionLoading(false);
    return response;
  };

  const handleTransferAction = async (selectedAddress, force = false) => {
    setActionLoading(true);
    const response = await deleteAddress({ selectedAddress, force });
    setActionLoading(false);
    return response;
  };

  const handleConfirmActionOnAddressModal = async ({ forceAction, forceTransfer, selectedAction, selectedAddress }) => {
    // Handle action selection submit
    if (selectedAction) {
      if (selectedAction === 'TRANSFER') {
        return setActionAddress();
      }

      // Selected action is DELETE
      const { conflict } = await handleDeleteAddress(selectedAction);
      if (conflict) return setActionConflictSelect(selectedAction);
      return resetShowAction();
    }

    // Handle confirmation on delete
    if (!isNil(forceAction)) {
      if (forceAction) {
        await handleDeleteAddress(previousChoice, true);
        return resetShowAction();
      }

      return setActionSelect();
    }

    // Handle Address selection submit
    if (selectedAddress) {
      const { conflict } = await handleTransferAction(selectedAddress);
      if (conflict) return setActionConflictTransfer(selectedAddress);
      return resetShowAction();
    }

    // Handle confirmation on transfer
    if (!isNil(forceTransfer)) {
      if (forceTransfer) {
        await handleTransferAction(previousChoice, true);
        return resetShowAction();
      }

      return setActionAddress();
    }

    // Handle preference passed
    if (sae.length > 0) return setActionSelect();
    setActionLoading(true);
    await deleteAddress({});
    setActionLoading(false);
    resetShowAction();
  };

  const handleCloseDialog = () => {
    setDeleteDialogStatus(DELETE_DIALOG_ENUM.HIDDEN);
  };

  const handleCancelForm = () => {
    if (isNewAddress) {
      setAddress(currentAddress ?? initialAddressState);
      setIsNewAddress(false);
    } else if (!formDisabled) {
      setAddress(selectedAddress);
    }

    setFormDisabled(true);
  };

  const handleSubmitForm = async (values) => {
    if (isNewAddress) return await handleCreateAddress(values);
    else return await handleEditAddress(values);
  };

  return (
    <Grid container direction="column" style={{ height: 'auto', marginTop: '16px', minHeight: '93vh' }}>
      <Grid className={classes.title} container item xs={12} justifyContent="center">
        <HeaderParameters image={place} noMargin text="Lieux des prestations" />
      </Grid>

      <Grid container item justifyContent="center" style={{ flexGrow: 0 }}>
        <Grid container item sm={12} md={9} alignItems="center" justifyContent="space-between">
          <Grid container item xs={11} justifyContent="center">
            <CustomSelect
              itemList={addresses}
              labelField="title"
              idField="id"
              selectedItem={address}
              onClick={onAddressClick}
              justify="center"
            />
          </Grid>

          <Grid container item xs={1} justifyContent="flex-end">
            <BurgerMenu>
              <BurgerMenu.Item disabled={isNewAddress} onClick={onAddNewAddressClick}>
                Ajouter
              </BurgerMenu.Item>
              <BurgerMenu.Item disabled={isNewAddress || addresses.length === 0} onClick={() => setFormDisabled(false)}>
                Modifier
              </BurgerMenu.Item>
              <BurgerMenu.Item disabled={isNewAddress || addresses.length === 0} onClick={onRemoveAddressClick}>
                Supprimer
              </BurgerMenu.Item>
            </BurgerMenu>
          </Grid>
        </Grid>
      </Grid>

      <Grid container item justifyContent="center" style={{ flexGrow: 1 }}>
        <AddressForm
          address={address}
          associations={sae}
          disabled={formDisabled}
          onCancel={handleCancelForm}
          onStatusChange={handleStatusChanged}
          onSubmit={handleSubmitForm}
        />
      </Grid>

      <DefaultModal
        info
        open={deleteDialogStatus === DELETE_DIALOG_ENUM.NOT_PRINCIPAL_ADDRESS}
        onClose={handleCloseDialog}
        title="Suppression d'un Lieu"
        square
      >
        <Box className={classes.dialogContent}>
          <Typography className={classes.modalText} style={{ marginTop: '25%' }}>
            Vous ne pouvez pas supprimer votre lieu principal !
          </Typography>
        </Box>
      </DefaultModal>

      <ActionOnAddressModal
        address={address}
        loading={actionLoading}
        modalType={actionType.DELETE}
        onClose={resetShowAction}
        onConfirm={handleConfirmActionOnAddressModal}
        showAction={showAction}
      />
    </Grid>
  );
}

export default AddressesParameters;
